// How a HOST wants a frame's supporting text drawn (record-layout widgets,
// owner 2026-10-23: "the technical notes … at most a hover/info icon, the same
// rule as the table cells' notes"; "series named by their metric name").
// Hosts set it; renderers read it through the shared components (FrameAnnotations,
// ChartLegend, the axis caption) — no renderer branches on its host.
import { createContext, useContext } from 'react'

export interface FrameDisplay {
  /** 'inline' (default): the notes as a list under the chart; 'icon': one
   *  info mark whose hover is the same text — never lost, never in the way. */
  notes: 'inline' | 'icon'
  /** measure / quantity slug → the name a person reads (the metric's own name). */
  measureLabels: Record<string, string>
  /** 'icon' mode: where the host wants the mark (its own heading row); absent → in place. */
  notesSlot?: HTMLElement | null
  /** Which surface hosts the frame — an option's `auto` may read it (the stat's `display`); a renderer
   *  never branches on WHO the host is, only on what the host declares. Absent → a dashboard/canvas. */
  surface?: 'record'
  /** Warning kinds the host leaves out of its notes (they describe how the answer travelled, not the data). */
  quiet?: readonly string[]
}

/** Notes about the answer's transport, not its data: a dashboard card (Sentinel's look) does not show them. */
export const TRANSPORT_NOTE_KINDS: readonly string[] = ['cached']

export const FrameDisplayContext = createContext<FrameDisplay>({ notes: 'inline', measureLabels: {} })

export function useFrameDisplay(): FrameDisplay {
  return useContext(FrameDisplayContext)
}

/** The name a person reads for a measure column: the host's label, else the key. */
export function measureLabel(labels: Record<string, string>, key: string): string {
  return labels[key] ?? key
}

/** A slug in words ("temperature" → "Temperature") — for a measure the catalog names no better. */
export function slugWords(slug: string): string {
  const words = slug.replace(/_/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : slug
}
